import { useState, useEffect, useMemo } from "react";
import { useFetch } from "hooks/api";
import { getHashValue } from "utils/getQueryValue";
import Analytics from "./Analytics";
import AnalyticsOverview from "./AnalyticsOverview";
import PaymentDiscovery from "components/custom/onboarding/subscription/PaymentDiscovery";
import { useCategoriesQuery } from "../hooks/useCategories";
import { useAppSelector } from "../redux/hooks";
import { PageContainer } from "../components/core/basic/PageContainer";
import { VStack, Wrap } from "../../styled-system/jsx";
import { css } from "../../styled-system/css";
import { ButtonGroup } from "../components/core/basic/ButtonGroup";
import BreadcrumbDates from "../components/core/blocks/BreadcrumbDates";
import { DatesProvider } from "../dates/DatesContext";
import { Select } from "../components/fields/base/Select";

const marketplaceDict = {
  CA: "Canada",
  USA: "United States",
  MX: "Mexico",
  UK: "United Kingdom",
  NL: "Netherlands",
  ES: "Spain",
  IT: "Italy",
  FR: "France",
  IN: "India",
  AE: "UAE",
  SA: "Saudi Arabia",
  DE: "Germany",
  JP: "Japan",
  SG: "Singapore",
  AU: "Australia",
  SE: "Sweden",
  PL: "Poland",
  BE: "Belgium",
  TR: "Turkey",
  IE: "Ireland",
};

const AnalyticsView = () => {
  const defaultMarketplace = useAppSelector((state) => state.marketPlace.marketPlace);
  const user = useAppSelector((state) => state.user);

  const autoShow = getHashValue("new_shelf", "false");
  const [selectedMenu, setSelectedMenu] = useState(autoShow.toLowerCase() === "true" ? "shelves" : "overview");
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [selectedMarketplace, setSelectedMarketplace] = useState(defaultMarketplace);
  const [shelfRedirect, setShelfRedirect] = useState(null);
  const channel = "amazon";

  const categoriesQuery = useCategoriesQuery(selectedMarketplace, channel);

  const { data: marketplaceData } = useFetch(["marketplace", channel], "/api/organizations/marketplaces");

  const initiateShelfRedirect = (id) => {
    setShelfRedirect(parseInt(id));
    setSelectedMenu("shelves");
    // Then set marketplace
  };

  const marketplaceOptions = useMemo(() => {
    return Object.entries(marketplaceData?.data?.markets ?? {})
      .filter(([key]) => key in marketplaceDict)
      .map(([key]) => ({
        label: marketplaceDict[key],
        value: key,
      }));
  }, [marketplaceData]);

  useEffect(() => {
    if (categoriesQuery.data) {
      if (!categoriesQuery.data.find((c) => parseInt(c.id) === categoryFilter)) {
        setCategoryFilter(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarketplace]);

  if (!user.analytics_tier || ["unknown", "MANUALLY_DISABLED"].includes(user.analytics_tier)) {
    return (
      <div style={{ marginTop: "30px" }}>
        <PaymentDiscovery discoveryType={"search-analytics"} />
      </div>
    );
  }

  return (
    <DatesProvider>
      <BreadcrumbDates items={[{ name: "Search Analytics" }]} hideDateSelector={selectedMenu === "shelves"} hideCompare>
        <ButtonGroup
          buttons={[
            { label: "Overview", value: "overview" },
            { label: "Shelves", value: "shelves" },
          ]}
          value={selectedMenu}
          onSelect={setSelectedMenu}
          className={css({ alignSelf: "center" })}
        />
      </BreadcrumbDates>
      <PageContainer>
        <VStack gap="medium" padding="medium" alignItems="stretch">
          {selectedMenu === "shelves" ?
            <>
              <Wrap justify={"space-around"}>
                <Select
                  label={"Marketplace"}
                  selectedKey={selectedMarketplace}
                  onSelectionChange={setSelectedMarketplace}
                  items={marketplaceOptions}
                  className={css({ maxWidth: "200px" })}
                />

                <Select
                  label={"Category"}
                  selectedKey={categoryFilter ?? "all"}
                  onSelectionChange={(key) => setCategoryFilter(key === "all" ? null : key)}
                  items={[{ value: "all", label: "All Categories" }].concat(
                    categoriesQuery.data?.map((item) => ({
                      label: item.leaf_category_name,
                      value: item.id,
                    })) ?? [],
                  )}
                  className={css({ maxWidth: "200px" })}
                />
              </Wrap>

              <Analytics
                categoryFilter={categoryFilter}
                clearRedirect={setShelfRedirect}
                marketplace={selectedMarketplace}
                setCategoryFilter={setCategoryFilter}
                shelfRedirect={shelfRedirect}
              />
            </>
          : <AnalyticsOverview shelfRedirect={initiateShelfRedirect} marketplaceRedirect={setSelectedMarketplace} />}
        </VStack>
      </PageContainer>
    </DatesProvider>
  );
};

export default AnalyticsView;
